/**
 * Admin Query Logs API Client
 *
 * Fetches user query logs from CosmosDB for admin review.
 */

import type { ApiResult, ApiError } from './types';

/**
 * Query log request details
 */
export interface QueryLogRequest {
  query: string;
  expert_mode: boolean;
  as_of_date: string | null;
  include_superseded: boolean;
}

/**
 * Citation metadata
 */
export interface CitationMetadata {
  effective_date?: string;
  is_current?: boolean;
  authority_level?: string;
  authority_label?: string;
  authority_description?: string;
  regulation_title?: string;
  superseded_by_title?: string;
  superseded_by_date?: string;
}

/**
 * Citation from search results
 */
export interface Citation {
  title: string;
  content: string;
  id?: string;
  url?: string;
  metadata?: CitationMetadata;
}

/**
 * Query log response details
 */
export interface QueryLogResponse {
  answer: string;
  citations: Citation[];
  followups: string[];
  was_clarification: boolean;
  was_blocked: boolean;
  error: string | null;
}

/**
 * Query log metadata
 */
export interface QueryLogMetadata {
  response_time_ms: number;
  citation_count: number;
  model: string;
  search_results_count: number;
}

/**
 * Single query log entry
 */
export interface QueryLogEntry {
  id: string;
  timestamp: string;
  user_id: string;
  username: string;
  conversation_id: string;
  request: QueryLogRequest;
  response: QueryLogResponse;
  metadata: QueryLogMetadata;
}

/**
 * Query logs response from the API
 */
export interface QueryLogsResponse {
  logs: QueryLogEntry[];
  page: number;
  has_more: boolean;
}

/**
 * Query parameters for fetching logs
 */
export interface QueryLogsParams {
  start_date?: string;
  end_date?: string;
  username?: string;
  page_size?: number;
  page?: number;
}

// API base URL - empty string for same-origin, or full URL for cross-origin
const API_BASE_URL = import.meta.env.VITE_API_MANAGEMENT_URL || '';
const API_BASE = `${API_BASE_URL}/api/admin`;

/**
 * Fetch query logs from the admin API
 *
 * @param token - Auth0 access token
 * @param params - Optional filter parameters
 * @returns Paginated query logs or error
 */
export async function getQueryLogs(
  token: string,
  params: QueryLogsParams = {}
): Promise<ApiResult<QueryLogsResponse>> {
  try {
    const queryParams = new URLSearchParams();

    if (params.start_date) {
      queryParams.set('start_date', params.start_date);
    }
    if (params.end_date) {
      queryParams.set('end_date', params.end_date);
    }
    if (params.username) {
      queryParams.set('username', params.username);
    }
    if (params.page_size) {
      queryParams.set('page_size', params.page_size.toString());
    }
    if (params.page) {
      queryParams.set('page', params.page.toString());
    }

    const queryString = queryParams.toString();
    const url = `${API_BASE}/query-logs${queryString ? '?' + queryString : ''}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let error: ApiError;
      try {
        error = await response.json();
      } catch {
        error = {
          message: `Request failed with status ${response.status}`,
          code: `HTTP_${response.status}`,
        };
      }
      return { success: false, error };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    const error: ApiError = {
      message: err instanceof Error ? err.message : 'Network error',
      code: 'NETWORK_ERROR',
    };
    return { success: false, error };
  }
}
